import type {
  Datalayer,
  DatalayerStreamSideEffect,
  GDDLEvent,
} from '@analytics/models';
import { Injectable } from '@angular/core';

/**
 * Side Effect that tracks the duration of a page visit.
 * When a 'page-impression' event is received, it starts a debounce timer.
 * If no new 'page-impression' events are received within the debounce period,
 * a 'page-visit-duration' event is triggered to record the page visit duration.
 */
@Injectable()
export class PageVisitDurationSideEffect implements DatalayerStreamSideEffect {
  // Duration in seconds after which the page-visit-duration event will be fired
  private readonly seconds = 15;

  // Timeout reference used for debouncing the side effect
  private debounceTimeout: any = null;

  /**
   * When a 'page-impression' event is received, it triggers the creation of the debounced duration event.
   *
   * @param event$ The observable stream of GDDLEvents.
   * @returns The observable with the side effect (debounced duration event) applied.
   */
  process(event: GDDLEvent, datalayer: Datalayer) {
    if (event.event !== 'page-impression') {
      return;
    }
    // Reset the debounce timer every time this method is called.
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Set a new debounce timer to trigger after the specified duration
    this.debounceTimeout = setTimeout(() => {
      datalayer.pushEvent({
        event: 'page-visit-duration',
        info: { duration: this.seconds },
      });
    }, this.seconds * 1000);
  }
}
